import { Input, Select, Table, Tag } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminNotifications } from './Hooks';

const DELIVERY_STATUSES = ['PENDING', 'DELIVERED', 'FAILED', 'SKIPPED'];

const AdminNotificationsTab = () => {
  const { t } = useTranslation(['notifications', 'common']);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    notificationTypeCode: '',
    deliveryStatus: '',
    customerId: '',
  });

  const params = useMemo(
    () => ({
      page,
      limit: 20,
      ...(filters.notificationTypeCode
        ? { notificationTypeCode: filters.notificationTypeCode }
        : {}),
      ...(filters.deliveryStatus ? { deliveryStatus: filters.deliveryStatus } : {}),
      ...(filters.customerId ? { customerId: filters.customerId } : {}),
    }),
    [page, filters],
  );

  const { data, isLoading } = useAdminNotifications({ params });
  const rows = data?.paginatedData || [];
  const total = data?.totalCount || 0;

  const columns = [
    {
      title: t('sentAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
    },
    {
      title: t('typeCode'),
      dataIndex: 'notificationTypeCode',
      key: 'notificationTypeCode',
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: t('customer'),
      key: 'customer',
      render: (_, row) => {
        const customer = row.customerId;
        if (!customer || typeof customer !== 'object') return row.customerId || '—';
        const name = `${customer.fname || ''} ${customer.lname || ''}`.trim();
        return name || customer.phoneNumber || customer.email || '—';
      },
    },
    { title: t('title'), dataIndex: 'title', key: 'title', ellipsis: true },
    {
      title: t('deliveryStatus'),
      dataIndex: 'deliveryStatus',
      key: 'deliveryStatus',
      render: (status) => {
        const color =
          status === 'DELIVERED' ? 'green' : status === 'FAILED' ? 'red' : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: t('read'),
      dataIndex: 'isRead',
      key: 'isRead',
      render: (v) => (v ? t('yes') : t('no')),
    },
  ];

  return (
    <div className="mt-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Input
          allowClear
          placeholder={t('filterTypeCode')}
          value={filters.notificationTypeCode}
          onChange={(e) => {
            setPage(1);
            setFilters((prev) => ({ ...prev, notificationTypeCode: e.target.value }));
          }}
        />
        <Select
          allowClear
          placeholder={t('filterDeliveryStatus')}
          value={filters.deliveryStatus || undefined}
          onChange={(value) => {
            setPage(1);
            setFilters((prev) => ({ ...prev, deliveryStatus: value || '' }));
          }}
          options={DELIVERY_STATUSES.map((value) => ({ value, label: value }))}
        />
        <Input
          allowClear
          placeholder={t('filterCustomerId')}
          value={filters.customerId}
          onChange={(e) => {
            setPage(1);
            setFilters((prev) => ({ ...prev, customerId: e.target.value }));
          }}
        />
      </div>

      <Table
        rowKey="_id"
        loading={isLoading}
        columns={columns}
        dataSource={rows}
        pagination={{
          current: page,
          pageSize: 20,
          total,
          onChange: setPage,
          showSizeChanger: false,
        }}
        scroll={{ x: true }}
      />
    </div>
  );
};

export default AdminNotificationsTab;
